import React, { useCallback, useState, useRef, useEffect } from 'react';
import { css } from '@compiled/react';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/glyph/search';

const MIN_SEARCH_CHARS = 3;

const COUNTRY_OPTIONS = [
  'Åland Islands', 'Bonaire, Sint Eustatius and Saba', 'Saint Barthélemy', 'Curaçao',
  'Saint Martin (French Part)', 'South Sudan', 'Sint Maarten (Dutch part)',
  'United States Minor Outlying Islands', 'Luxembourg', 'Netherlands', 'Belgium',
  'Vatican City', 'Gibraltar', 'San Marino', 'Andorra', 'Monaco', 'Japan', 'Guam',
  'New Caledonia', 'Niue', 'Norfolk Island', 'Pitcairn', 'Bermuda', 'Falkland Islands',
  'Guyana', 'Canada', 'Jamaica', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Trinidad and Tobago', 'United States of America',
  'Bolivia', 'Brazil', 'Chile', 'Colombia', 'Ecuador', 'French Guiana', 'Netherlands Antilles',
  'Martinique', 'Montserrat', 'Puerto Rico', 'Virgin Islands (British)', 'Virgin Islands (US)',
  'Turks and Caicos Islands', 'Mexico', 'Tokelau', 'Wallis and Futuna Islands', 'India',
  'Iran', 'Afghanistan', 'Kazakhstan', 'Tajikistan', 'Turkmenistan', 'Uzbekistan', 'Bhutan',
  'Northern Mariana Islands', 'Nepal', 'Sri Lanka', 'Timor-Leste', 'Taiwan', 'Hong Kong',
  'Macau', 'Korea (South)', 'Korea (North)', 'China', 'Mongolia', 'Vietnam', 'Indonesia',
  'Thailand', 'Myanmar', 'French Southern Territories', 'Bosnia and Herzegovina', 'Italy',
  'Germany', 'Montenegro', 'Slovenia', 'Albania', 'Laos', 'Liechtenstein', 'Switzerland',
  'Austria', 'Macedonia', 'Croatia (Hrvatska)', 'Serbia', 'Greece', 'Portugal', 'Spain',
  'France', 'Marshall Islands', 'Micronesia', 'Nauru', 'New Zealand', 'Papua New Guinea',
  'Solomon Islands', 'Tonga', 'Tuvalu', 'Vanuatu', 'Samoa', 'Bangladesh', 'Maldives',
  'Pakistan', 'Brunei', 'Australia', 'Fiji', 'Cambodia', 'Malaysia',
  'Heard and McDonald Islands', 'Philippines', 'Singapore', 'Palau', 'Malta', 'Nicaragua',
  'Panama', 'Saint Pierre and Miquelon', 'South Georgia and the South Sandwich Islands',
  'Venezuela', 'Paraguay', 'Peru', 'Suriname', 'Uruguay', 'Greenland', 'Kiribati',
  'Kyrgyzstan', 'American Samoa', 'Antarctica', 'Christmas Island', 'Cocos (Keeling) Islands',
  'Antigua and Barbuda', 'Cook Islands', 'Aruba', 'Argentina', 'Latvia', 'French Polynesia',
  'Lithuania', 'Sao Tome and Principe', 'Barbados', 'Dominican Republic', 'Grenada', 'Belize',
  'Cayman Islands', 'Cuba', 'Dominica', 'Guadeloupe', 'Haiti', 'Costa Rica', 'Senegal',
  'El Salvador', 'Guatemala', 'Honduras', 'Bahamas', 'Anguilla', 'Somalia', 'Sudan', 'Tunisia',
  'Western Sahara', 'Angola', 'Slovak Republic', 'Russian Federation', 'Congo (Republic of)',
  'Cote d\'Ivoire (Ivory Coast)', 'Reunion', 'Congo (Democratic People\'s Republic)',
  'Iceland', 'Norway', 'Sweden', 'Djibouti', 'Egypt', 'Palestinian Territories', 'Syria',
  'Bahrain', 'United Kingdom', 'Swaziland', 'Tanzania', 'Uganda', 'Zambia', 'Zimbabwe',
  'Ireland', 'Cape Verde', 'Central African Republic', 'Chad', 'Comoros', 'Estonia', 'Georgia',
  'Equatorial Guinea', 'Gabon', 'Guinea-Bissau', 'Guinea', 'Libya', 'Algeria', 'Moldova',
  'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mayotte', 'Morocco', 'Mozambique', 'Niger',
  'Poland', 'Romania', 'Ukraine', 'Israel', 'Iraq', 'Jordan', 'Kuwait', 'Lebanon', 'Oman',
  'Qatar', 'Saudi Arabia', 'Turkey', 'United Arab Emirates', 'Denmark', 'Finland', 'Belarus',
  'Faroe Islands', 'Guernsey', 'Isle of Man', 'Jersey', 'Svalbard and Jan Mayen Islands',
  'Azerbaijan', 'Cyprus', 'Armenia', 'Saint Helena', 'Cameroon', 'Gambia', 'Ghana', 'Kenya',
  'Lesotho', 'Liberia', 'Mauritius', 'Namibia', 'Rwanda', 'Seychelles', 'Sierra Leone',
  'South Africa', 'Togo', 'Botswana', 'Bulgaria', 'Czech Republic', 'Nigeria', 'Hungary',
  'Benin', 'British Indian Ocean Territory', 'Burkina Faso', 'Burundi', 'Yemen',
  'Bouvet Island', 'Ethiopia', 'Eritrea',
];

type Props = {
  value: string;
  onChange: (value: string) => void;
  isInvalid?: boolean;
  inputId: string;
};

/**
 * Salesforce-style Country field: search input with magnifying glass on left,
 * "Search..." placeholder, no dropdown arrow. Shows filtered options when typing 3+ chars.
 */
export const CountrySearchField: React.FC<Props> = ({
  value,
  onChange,
  isInvalid,
  inputId,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [justSelected, setJustSelected] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayValue = isFocused && !justSelected ? searchInput : value;
  const showDropdown = isOpen && searchInput.length >= MIN_SEARCH_CHARS;

  const filteredOptions = useCallback(() => {
    if (searchInput.length < MIN_SEARCH_CHARS) return [];
    const lower = searchInput.toLowerCase();
    return COUNTRY_OPTIONS.filter((c) => c.toLowerCase().includes(lower)).slice(0, 10);
  }, [searchInput]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = (e.target as HTMLInputElement).value;
    setSearchInput(v);
    setIsOpen(true);
  };

  const handleSelect = (country: string) => {
    onChange(country);
    setSearchInput('');
    setIsOpen(false);
    setJustSelected(true);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setSearchInput('');
    setJustSelected(false);
    setIsOpen(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setTimeout(() => setIsOpen(false), 200);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const options = filteredOptions();

  return (
    <div ref={containerRef} css={containerStyles} className="champion-form-country-search">
      <Textfield
        id={inputId}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isCompact
        placeholder="Search..."
        isInvalid={isInvalid}
        elemBeforeInput={
          <span css={searchIconStyles}>
            <SearchIcon label="" size="small" />
          </span>
        }
      />
      {showDropdown && options.length > 0 && (
        <div css={dropdownStyles}>
          {options.map((country) => (
            <div
              key={country}
              css={optionStyles}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(country);
              }}
            >
              {country}
            </div>
          ))}
        </div>
      )}
      {showDropdown && searchInput.length >= MIN_SEARCH_CHARS && options.length === 0 && (
        <div css={dropdownStyles}>
          <div css={noOptionsStyles}>No countries found</div>
        </div>
      )}
    </div>
  );
};

const containerStyles = css({
  position: 'relative',
  width: '100%',
});

const searchIconStyles = css({
  display: 'flex',
  alignItems: 'center',
  color: '#706e6b',
  paddingLeft: '0.5rem',
});

const dropdownStyles = css({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '2px',
  backgroundColor: '#fff',
  border: '1px solid #c9c9c9',
  borderRadius: '0.25rem',
  boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.16)',
  maxHeight: '150px',
  overflowY: 'auto',
  zIndex: 100,
});

const optionStyles = css({
  padding: '0.5rem 0.75rem',
  fontSize: '0.8125rem',
  color: '#080707',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: '#f3f2f2',
  },
});

const noOptionsStyles = css({
  padding: '0.5rem 0.75rem',
  fontSize: '0.8125rem',
  color: '#706e6b',
});
